import {
  createParser,
  createSerializer,
  parseAsArrayOf,
  parseAsString,
  useQueryStates,
} from 'nuqs';

export type GroupFilters = {
  orgs: string[];
  periods: string[];
};

// Default to current calendar year. Matches the mock GroupPeriodPicker's
// year-{YYYY} synthetic UUIDs. Phase 2 will source defaults from the
// backend's group account response.
const DEFAULT_PERIODS = ['year-2025'];

/**
 * Periods need a sentinel: a missing `periods` param means "apply the default
 * year", while an explicitly cleared filter ("all time periods") serialises as
 * `periods=all` — so a shared/refreshed URL keeps the cleared state instead of
 * snapping back to the default.
 */
const parseAsPeriods = createParser<string[]>({
  parse: (value) => (value === 'all' ? [] : value.split(',').filter(Boolean)),
  serialize: (value) => (value.length === 0 ? 'all' : value.join(',')),
  eq: (a, b) => a.length === b.length && a.every((v, i) => v === b[i]),
});

const groupFilterParsers = {
  orgs: parseAsArrayOf(parseAsString).withDefault([]),
  periods: parseAsPeriods.withDefault(DEFAULT_PERIODS),
};

/**
 * Builds a search string (`?orgs=…&periods=…`) for the given filter values,
 * optionally on top of a base path. Values equal to their defaults are
 * omitted. Used by drillthrough links to target a page with filters applied.
 */
export const serializeGroupFilters = createSerializer(groupFilterParsers);

/**
 * URL state for the group dashboard's global filters — the URL is the single
 * source of truth, mirroring the data-table filter convention. Empty `orgs`
 * means "all organisations".
 *
 * Because the state lives in the URL, every intra-group link must carry the
 * current params forward (sidebar, dashboard cards, drillthroughs) — use
 * useGroupFilterSearch / serializeGroupFilters for that. Plain links drop the
 * selection back to the defaults.
 */
export function useGroupFilters() {
  return useQueryStates(groupFilterParsers);
}

/**
 * The current filters as a ready-to-append search string ('' when everything
 * is at its default). Use to preserve filters across intra-group navigation:
 * `<Link to={GROUP_ROUTES.PRODUCTS + filterSearch} />`.
 */
export function useGroupFilterSearch(): string {
  const [filters] = useGroupFilters();
  return serializeGroupFilters(filters);
}
